import { useEffect, useRef, useState, useCallback } from 'react'
import { withStyles } from '@aura/components/styles'
import Button from '@aura/components/button'
import Grid from '@aura/components/grid'
import HorizontalGroup from '@aura/components/horizontal-group'
import Link from '@aura/components/link'
import CircularProgress from '@aura/components/circular-progress'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import InfoOutlineIcon from '@mui/icons-material/InfoOutline'
import Typography from '@aura/components/typography'
import Box from '@aura/components/box'
import Form from '@aura/forms/form'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import recoveryTypeList from '../../../../constants/recovery-types'
import RegistrationContainerNew from '../../../../components/registration-container-new'
import { hideLoader } from '../../../../utils/loader-actions'
import AccountRecoveryTypes from '../../../../constants/factor-types'
import formFields, { initialValues } from './constants/form-fields'
import styles from './styles'


const SMS_OTP_LENGTH = 6
const CALL_OTP_LENGTH = 5
const getOtpLength = factorType => (factorType === AccountRecoveryTypes.CALL ? CALL_OTP_LENGTH : SMS_OTP_LENGTH)
const createOtpArray = length => Array(length).fill('')


const Component = ({
    classes,
    data,
    error,
    isBackAllowed,
    isProcessing,
    isVerified,
    onBack,
    onNext,
    onResendCode,
    onResendSuccess,
    OnValidatePhoneAtLogin,
    selectedRecoveryType,
    validatePhoneAtLogin,
}) => {

    const { t: translate, i18n } = useTranslation()
    const ref = useRef()
    const inputRefs = useRef([])
    const [ otp, setOtp ] = useState(() => createOtpArray(getOtpLength(data?.factorType)))
    const [ successMsg, setSuccessMsg ] = useState('')
    const [ timer, setTimer ] = useState(30)
    const [ canResend, setCanResend ] = useState(false)
    const [ nextButtonDisabled, setNextButtonDisabled ] = useState(true)
    const [ hasError, setHasError ] = useState(false)

    useEffect(() => {
        hideLoader()
    }, [])

    useEffect(() => {
        onResendSuccess && setSuccessMsg(translate('recoveryValidation.resendSuccessMsg'))
    }, [ onResendSuccess, i18n.language ])

    useEffect(() => {
        const { setFieldError } = ref.current.getContext()
        if (error) {
            setFieldError(PASSCODE, translate('oktaApiResponse.' + `${error}`))
            setHasError(true)
        } else {
            setHasError(false)
        }
    }, [ error, i18n.language ])

    const { PASSCODE } = formFields
    const { phoneExtension, phoneNumber, factorType } = data
    const otpLength = otp.length

    useEffect(() => {
        const otpValue = otp.join('')
        if (ref.current) {
            const { setFieldValue } = ref.current.getContext()
            setFieldValue(PASSCODE, otpValue)
        }
        setNextButtonDisabled(otpValue.length !== otpLength)
    }, [ otp, PASSCODE, otpLength ])

    const prevFactorTypeRef = useRef(factorType)
    useEffect(() => {
        // Only reset OTP when factorType actually changes to a different valid value
        // and not when verification is complete
        if (factorType && factorType !== prevFactorTypeRef.current && !isVerified) {
            setOtp(createOtpArray(getOtpLength(factorType)))
            inputRefs.current = []
            prevFactorTypeRef.current = factorType
        }
    }, [ factorType, isVerified ])

    const getFirstEmptyIndex = useCallback(() => {
        return otp.findIndex(digit => digit === '')
    }, [ otp ])

    const handleOtpChange = useCallback((index, value) => {
        // Only allow numbers
        if (value && !/^\d$/.test(value)) {
            return
        }

        const firstEmptyIndex = getFirstEmptyIndex()

        if (value && firstEmptyIndex !== -1 && index !== firstEmptyIndex) {
            const newOtp = [ ...otp ]
            newOtp[firstEmptyIndex] = value
            setOtp(newOtp)
            setHasError(false)
            if (firstEmptyIndex < otp.length - 1) {
                setTimeout(() => {
                    inputRefs.current[firstEmptyIndex + 1]?.focus()
                }, 0)
            }
            return
        }

        const newOtp = [ ...otp ]
        newOtp[index] = value
        setOtp(newOtp)
        setHasError(false)

        if (value && index < otp.length - 1) {
            setTimeout(() => {
                inputRefs.current[index + 1]?.focus()
            }, 0)
        }
    }, [ otp, getFirstEmptyIndex ])

    const handleOtpKeyDown = useCallback((index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus()
        }
        if (e.key === 'Enter') {
            const otpValue = otp.join('')
            if (otpValue.length === otp.length && !isProcessing) {
                onNext({ passCode: otpValue })
            }
        }
    }, [ otp, isProcessing, onNext ])

    const handleOtpPaste = useCallback(e => {
        e.preventDefault()
        const maxOtpLength = otp.length
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, maxOtpLength)
        if (pastedData) {
            const newOtp = [ ...otp ]
            for (let i = 0; i < pastedData.length && i < maxOtpLength; i++) {
                newOtp[i] = pastedData[i]
            }
            setOtp(newOtp)
            // Focus the next empty field or the last field
            const nextIndex = Math.min(pastedData.length, Math.max(maxOtpLength - 1, 0))
            inputRefs.current[nextIndex]?.focus()
        }
    }, [ otp ])

    useEffect(() => {
        let countdown
        const startCountdown = () => {
            countdown = setTimeout(() => {
                setTimer(prevTimer => prevTimer - 1)

                if (timer <= 1) {
                    setCanResend(true)
                }

                if (timer > 1) {
                    startCountdown()
                }
            }, 1000)
        }

        startCountdown()

        return () => {
            if (countdown) {
                clearTimeout(countdown)
            }
        }
    }, [ timer ])

    const handleResendCode = useCallback(async event => {
        event.preventDefault()
        if (canResend) {
            setSuccessMsg('')
            setOtp(createOtpArray(getOtpLength(factorType)))
            setHasError(false)
            if (validatePhoneAtLogin) {
                OnValidatePhoneAtLogin({ recoveryType: selectedRecoveryType })
            } else {
                onResendCode({ recoveryType: selectedRecoveryType })
            }
            setCanResend(false)
            setTimer(30)
            // Focus first input after resend
            setTimeout(() => inputRefs.current[0]?.focus(), 100)
        }
    }, [ canResend, validatePhoneAtLogin, selectedRecoveryType ])

    const validate = values => {
        const errors = {}
        setSuccessMsg('')
        if (!values[PASSCODE]) {
            errors[PASSCODE] = translate('required')
            setNextButtonDisabled(true)
        } else {
            setNextButtonDisabled(false)
        }

        console.log('errors', errors)
        return errors
    }

    const recoveryType = recoveryTypeList.find(rec => rec.value === factorType)?.label
    const recoveryTypeTranslated = translate(recoveryType)
    const title = data?.factorType === AccountRecoveryTypes.CALL ? translate('loginFlow.accountRecovery.verificationCall') : translate('loginFlow.accountRecovery.verificationSMS')
    const verbiage = data?.factorType === AccountRecoveryTypes.CALL ? translate('loginFlow.accountRecovery.verificationVerbiageCall', { phoneNumber: phoneExtension ? phoneNumber + ' x'+ phoneExtension : phoneNumber, recoveryType: recoveryTypeTranslated }) : translate('loginFlow.accountRecovery.verbiage', { phoneNumber: phoneNumber, recoveryType: recoveryTypeTranslated })

    const footerContent = (<Grid className={classes.buttonContainer} container item md={12} xs={12}>
        <Grid container className={classes.actionRow} justify="flex-end">
            <HorizontalGroup gutter={10}>
                {isBackAllowed
                    ? <Button
                        className={classes.buttonSkip}
                        onClick={onBack}
                        variant="text"
                        disabled={isProcessing}>{translate('back')}
                    </Button>
                    : <></>
                }
                <Button
                    className={classes.buttonSubmit}
                    onClick={() => onNext({ passCode: otp.join('') })}
                    variant="contained"
                    disabled={isProcessing || nextButtonDisabled}>{translate('next')}
                </Button>
            </HorizontalGroup>
        </Grid>
    </Grid>)

    const containerProps = isVerified
        ? { title, verbiage }
        : { footerActions: footerContent, title, verbiage }

    return (
        <RegistrationContainerNew {...containerProps}>
            <Form className={classes.formContainer} {...{
                enableReinitialize: true,
                initialValues: initialValues,
                onSubmit: onNext,
                ref,
                validate
            }}>
                <div className={classes.fieldsContainer}>
                    <Grid className={classes.verificationContainer} container spacing={2}>
                        <Box className={data?.factorType === AccountRecoveryTypes.CALL ? classes.verificationCodeBoxCall : classes.verificationCodeBox}>
                            <Typography variant="body2" className={classes.otpLabel}>
                                {translate('loginFlow.accountRecovery.verificationCode')}
                            </Typography>
                            <Box display="flex" gap={1} justifyContent="center">
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={el => inputRefs.current[index] = el}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={e => handleOtpChange(index, e.target.value)}
                                        onKeyDown={e => handleOtpKeyDown(index, e)}
                                        onPaste={handleOtpPaste}
                                        onFocus={() => {
                                            const firstEmpty = otp.findIndex(d => d === '')
                                            if (firstEmpty !== -1 && index !== firstEmpty) {
                                                setTimeout(() => {
                                                    inputRefs.current[firstEmpty]?.focus()
                                                }, 0)
                                            }
                                        }}
                                        autoFocus={index === 0}
                                        className={`${classes.otpInput} ${hasError ? classes.otpInputError : ''} ${digit ? classes.otpInputFilled : ''} ${isVerified ? classes.otpInputVerified : ''}`}
                                        aria-label={`Digit ${index + 1}`}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Grid>
                    <Grid container className={classes.verificationCodeContainer} spacing={2} alignItems="center">
                        {(error && !isVerified) && (
                            <Box className={classes.errorContainer}>
                                <InfoOutlineIcon color='error' fontSize="small" />
                                <Typography variant="caption" color="error" className={classes.errorText}>
                                    {translate('oktaApiResponse.' + `${error}`)}
                                </Typography>
                            </Box>
                        )}
                        {isVerified && (<Box className={classes.successContainer}>
                            <CheckCircleOutlineIcon className={classes.checkCircle} color='success' />
                            <Typography variant="caption" color="success" className={classes.successMsg}>
                                {translate('loginFlow.accountRecovery.verified')}
                            </Typography>
                        </Box>)}
                        {successMsg && (<div className={classes.successMsg}>
                            {successMsg}
                        </div>)}
                        {!isVerified && <Box className={classes.messageContainer}>
                            <Link
                                className={canResend ? classes.link : classes.disabledLink}
                                href=""
                                onClick={handleResendCode}
                                underline="none">{canResend ? translate('loginFlow.accountRecovery.resendCode') : translate('loginFlow.accountRecovery.enterVerificationCode')}
                            </Link>
                            {!canResend && (
                                <div className={classes.countdown}>
                                    <CircularProgress variant="determinate" className={classes.countdownProgress} value={(timer / 30) * 100} size={30} thickness={5} />
                                    <Typography variant="caption" className={classes.countdownTypography}>{timer}</Typography>
                                </div>
                            )}
                        </Box>}
                    </Grid>
                </div>
                <Box className={isVerified ? classes.warningLineVerified : classes.warningLine}>{translate('mfaEnrollment.verbiage07')} </Box>
            </Form>
        </RegistrationContainerNew>
    )
}

Component.displayName = 'Verification'

Component.defaultProps = {
    data: {
        phoneNumber: ''
    },
    isBackAllowed: true
}

Component.propTypes = {
    classes: PropTypes.shape({
        button: PropTypes.object,
        loader: PropTypes.object,
        root: PropTypes.object
    }),
    data: PropTypes.object.isRequired,
    error: PropTypes.string,
    isBackAllowed: PropTypes.bool.isRequired,
    onBack: PropTypes.func.isRequired,
    onNext: PropTypes.func.isRequired,
    onResendCode: PropTypes.func.isRequired,
    onResendSuccess: PropTypes.string,
    onSave: PropTypes.func.isRequired
}

export default withStyles(Component, styles)
